import { type NornClient, type Refusal, type Schemas, NornRefusal, createNornClient, expectOk } from "@norn/sdk"
import type { Connection, Scope } from "./connection"
import { type Notice, NoticeError } from "./i18n"

export type { NornClient, Refusal, Schemas }

/** The cell could not be reached at all (DNS, TLS, CORS, offline). */
export class CellUnreachable extends Error {
  constructor(
    readonly baseUrl: string,
    cause: unknown,
  ) {
    super(`Could not reach ${baseUrl}`, { cause })
  }
}

/** A non-refusal HTTP failure (proxy error page, 5xx without a refusal body). */
export class HttpFailure extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
  }
}

export type ApiError = NornRefusal | CellUnreachable | HttpFailure

export function clientFor(connection: Connection, fetchImpl?: typeof fetch): NornClient {
  return createNornClient({
    baseUrl: connection.baseUrl,
    ...(connection.token ? { token: connection.token } : {}),
    ...(connection.actor ? { actor: connection.actor } : {}),
    ...(fetchImpl ? { fetch: fetchImpl } : {}),
  })
}

/**
 * Await one SDK call: its data, or a typed {@link ApiError}. Every refusal keeps
 * its kernel category so the UI can say what the operator can do about it.
 */
export async function run<T>(baseUrl: string, call: () => Promise<{ readonly data?: T; readonly error?: unknown; readonly response: Response }>): Promise<T> {
  let result: Awaited<ReturnType<typeof call>>
  try {
    result = await call()
  } catch (cause) {
    throw new CellUnreachable(baseUrl, cause)
  }
  // openapi-fetch 0.17 JSON-decodes error bodies for every parseAs; decode a string body here too so a
  // refusal keeps its category even if a client version hands the raw text through.
  if (typeof result.error === "string") {
    try {
      result = { ...result, error: JSON.parse(result.error) as unknown }
    } catch {
      // Not JSON: reported below as an HTTP failure with its text.
    }
  }
  try {
    return expectOk(result)
  } catch (error) {
    if (error instanceof NornRefusal) throw error
    throw new HttpFailure(result.response.status, error instanceof Error ? error.message : String(error))
  }
}

/** The explicit scope query every scoped read and task operation carries. */
export function scopeQuery(scope: Scope): { orgId: string; environmentId: string } {
  return { orgId: scope.orgId, environmentId: scope.environmentId }
}

export function refusalOfError(error: unknown): { status: number; refusal: Refusal } | null {
  return error instanceof NornRefusal ? { status: error.status, refusal: error.refusal } : null
}

/** Query keys are namespaced by cell and scope so switching either never shows stale data. */
export const keys = {
  scope: (baseUrl: string, scope: Scope) => [baseUrl, scope.orgId, scope.environmentId] as const,
  defs: (baseUrl: string, scope: Scope, version?: number) => ["defs", baseUrl, scope.orgId, scope.environmentId, version ?? "active"] as const,
  objects: (baseUrl: string, scope: Scope, type: string, cursor: string | null) => ["objects", baseUrl, scope.orgId, scope.environmentId, type, cursor] as const,
  object: (baseUrl: string, scope: Scope, type: string, key: string) => ["object", baseUrl, scope.orgId, scope.environmentId, type, key] as const,
  why: (baseUrl: string, scope: Scope, type: string, key: string) => ["why", baseUrl, scope.orgId, scope.environmentId, type, key] as const,
  ledger: (baseUrl: string, scope: Scope, after: number, limit: number) => ["ledger", baseUrl, scope.orgId, scope.environmentId, after, limit] as const,
}

/** What to tell the operator about a failure, translated when shown (see {@link Notice}). */
export function errorNotice(error: unknown): Notice {
  if (error instanceof NoticeError) return error.notice
  if (error instanceof CellUnreachable) return { key: "common.couldNotReach", values: { url: error.baseUrl } }
  return { text: error instanceof Error ? error.message : String(error) }
}
